<template>
  <slot v-if="isAppReady"></slot>
  <BaseInnerLoading v-else />
</template>

<script setup lang="ts">
import {
  ApiError,
  FailedToFetchError,
  FetchTimedOutError,
} from "@/services/api"
import { UserVisibleError } from "@/utils/UserVisibleError"
import { pairDevice } from "@/services/pairDevice"
import * as Sentry from "@sentry/vue"
import { IDevice } from "@/androidPlugins/IDevice"
import BaseInnerLoading from "./BaseInnerLoading.vue"
const { accessToken, setBaseToken, isLoggedIn, isPaired } =
  useAuth()
const {
  serialNumber,
  deviceSecret,
  merchantId,
  hydrationRequired,
  appVersion,
  hydrateDeviceState,
} = useDevice()
const router = useRouter()
const { setTenantState, setIsUKMerchant } = useTenant()
const { getOverrideMid } = useOverrideMid()

// Can't watch access token directly to determine if paired, as this results in
// premature rendering of RouterView before pairing process has determine route
const isPairing = ref(false)

const isAppReady = computed(
  () => !isPairing.value && isPaired.value
)

const { setCanHideSplashScreen } = useSplashScreenState()

whenever(isAppReady, () => setCanHideSplashScreen(true))

watch(
  hydrationRequired,
  async (value) => {
    if (value) {
      await hydrateDeviceState()
    }
  },
  { immediate: true }
)

watch(
  [
    serialNumber,
    deviceSecret,
    merchantId,
    accessToken,
    appVersion,
  ],
  async ([
    newSerial,
    newSecret,
    newMerchantId,
    newToken,
    newAppVersion,
  ]) => {
    const canPair =
      newSerial !== undefined &&
      newSecret !== undefined &&
      newMerchantId !== undefined &&
      newAppVersion !== undefined

    const requiresPair = newToken === null
    const {
      model,
      operatingSystem,
      osVersion,
      webViewVersion,
    } = await IDevice.getInfo()

    if (canPair && requiresPair) {
      isPairing.value = true

      try {
        const overrideMid =
          import.meta.env.VITE_ENVIRONMENT === "production"
            ? null
            : await getOverrideMid()

        setIsUKMerchant(
          !(
            import.meta.env.VITE_ORGANIZATION === "boipa"
          ) ||
            (overrideMid ?? newMerchantId)
              .trim()
              .toUpperCase()
              .startsWith("GB")
        )

        const body = await pairDevice({
          merchantId: overrideMid ?? newMerchantId,
          deviceSecret: newSecret,
          serialNumber: newSerial,
          appVersion: newAppVersion,
          model: model,
          operatingSystem: `${operatingSystem} ${osVersion}`,
          platform: import.meta.env.VITE_PLATFORM,
          webViewVersion,
        })

        Sentry.setUser({
          id: overrideMid ?? newMerchantId,
          username: body.onboardingComplete
            ? body.tenantState.name
            : undefined,
        })

        Sentry.setTag("device.serial_number", newSerial)

        setBaseToken(body.accessToken)

        if (!body.onboardingComplete) {
          await router.push({ name: "onboarding" })
        } else if (isLoggedIn.value) {
          setTenantState(body.tenantState)
          await router.push({ name: "home" })
        } else {
          setTenantState(body.tenantState)
          await router.push({ name: "logInIndex" })
        }

        isPairing.value = false
      } catch (error) {
        if (error instanceof ApiError) {
          const message =
            error.code === "merchant-not-found"
              ? "Merchant not found"
              : error.code === "device-not-found"
                ? "Device not recognised"
                : error.code === "merchant-disabled"
                  ? "Merchant is disabled"
                  : "Error pairing device"

          if (message === "Error pairing device") {
            Sentry.captureException(error)
          }
          throw new UserVisibleError(message)
        } else if (error instanceof UserVisibleError) {
          throw error
        } else if (error instanceof FailedToFetchError) {
          Sentry.captureException(error)

          throw new UserVisibleError(
            "Pairing failed: network error"
          )
        } else if (error instanceof FetchTimedOutError) {
          Sentry.captureException(error)

          throw new UserVisibleError(
            "Pairing failed: request timed out"
          )
        } else {
          Sentry.captureException(error)

          throw new UserVisibleError("Error pairing device")
        }
      }
    }
  },
  {
    immediate: true,
  }
)
</script>
